/**
 * Copyright by INSIDE M2M GmbH and the authors 2004 - 2018
 *
 *************************************************************
 ********************* CONFIDENTIAL **************************
 *************************************************************
 *
 */

import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Action, createAction } from '@ngrx/store';
import cloneDeep from 'lodash-es/cloneDeep';

export function updateFunction<T>(oldEntity: T, updatedEntity: T): T {
    const e = cloneDeep(oldEntity);
    return Object.assign(e, updatedEntity);
}

function updateEntity<T>(list: T[], updatedEntity: T, idProperties: [keyof T], updateFn: (oldEntity: T, updatedEntity: T) => T) {
    const index = list.findIndex((e) =>
        idProperties.reduce(
            (prev, curr) => prev || (e[curr] === updatedEntity[curr] && e[curr] !== undefined && updatedEntity[curr] !== undefined),
            false
        )
    );
    if (index !== -1) {
        list.splice(index, 1, updateFn(list[index], updatedEntity));
    } else {
        list.push(updatedEntity);
    }
    return list[index];
}

/**
 * Update a list of immutable entities. If the entities do not exist, they are added to the list.
 * @param list The list in which the entities should be updated.
 * @param updatedEntities The updated entities.
 * @param idProperties The properties by which the entities should be identified.
 */
export function createOrUpdateEntities<T>(list: T[], updatedEntities: T[], idProperties: [keyof T], updateFn = updateFunction) {
    const updatedList = list.slice();
    for (const entity of updatedEntities) {
        updateEntity(updatedList, entity, idProperties, updateFn);
    }
    return updatedList;
}

/**
 * This is a type-safe version of Object#assign.
 *
 * @param target The object whose interface the sources should comply to.
 * @param sources Objects whose properties should be assigned to the target object's properties.
 */
export function assign<T>(target: T, ...sources: Array<Readonly<Partial<T>>>): T {
    const result: any = cloneDeep(target);
    for (const source of sources) {
        for (const property in source) {
            // eslint-disable-next-line no-prototype-builtins, @typescript-eslint/ban-types
            if ((source as object).hasOwnProperty(property)) {
                result[property] = source[property];
            }
        }
    }
    return result;
}

/**
 * Returns whether a property of the given iteratee matches the filterString.
 * Can be used for functions like {@link lodash#filter}.
 *
 * @param iteratee Object whose properties should be matched with the filterString.
 * @param filterString String to match.
 */
export function filterFunction(iteratee: any, filterString?: string): boolean {
    if (!filterString || filterString === '') {
        return true;
    }
    return (
        Object.keys(iteratee)
            .map((prop) => String(iteratee[prop]).toLowerCase().indexOf(filterString.toLowerCase()) !== -1)
            .indexOf(true) !== -1
    );
}

export const RouterNavigationActionType = '[app] ROUTER_NAVIGATION';
export const ResetAppStateActionType = '[app] RESET_APP_STATE';

export class RouterNavigationAction implements Action {
    readonly type = RouterNavigationActionType;
    payload: {
        url: string;
        path: string;
        params: { [key: string]: string };
        queryParams: { [key: string]: string };
    };
    constructor(routerState: RouterStateSnapshot) {
        const url = routerState.url;
        const queryParams = routerState.root.queryParams;
        const params = this.getParams(routerState.root, []).reduce((previous, current) => ({ ...previous, ...current }), {});
        const path = url.split('?')[0];
        this.payload = {
            url,
            queryParams,
            params,
            path,
        };
    }

    getParams(routerState: ActivatedRouteSnapshot, params: any[]): any[] {
        return params.concat(
            routerState.params,
            ...routerState.children.map((s) => {
                if (s.children.length > 0) {
                    return this.getParams(s, params);
                } else {
                    return s.params;
                }
            })
        );
    }
}

export const resetAppState = createAction(ResetAppStateActionType);
